import type { MbcBrandContext } from "@nlc/shared-types";

import { useNavigate, useRouter } from "@tanstack/react-router";
import { useCallback, useState } from "react";

import { useGetBrandById } from "@/features/brands/shared/hooks/useGetBrandById";

import { BrandGate } from "./components/BrandGate";
import { MBCChatHeader } from "./components/MBCChatHeader";
import { MBCConversation } from "./components/MBCConversation";
import { mapBrandContext } from "./lib/map-brand-context";

type MBCChatPageProps = {
  /** Numeric design-type id coming from the request-create picker. */
  designType?: number;
  /** Project the request is being created for; scopes the brand list/drafts. */
  projectId?: number;
  /** Resume: brand of the draft being reopened (re-grounds the agent). */
  resumeBrandId?: number;
  /** Resume: the draft's resource id. */
  resumeResourceId?: string;
  /** Resume: the draft's thread id. Presence = resume mode. */
  resumeThreadId?: string;
};

export function MBCChatPage({
  designType,
  projectId,
  resumeBrandId,
  resumeResourceId,
  resumeThreadId,
}: MBCChatPageProps) {
  const navigate = useNavigate();
  const router = useRouter();
  const handleBack = useCallback(() => router.history.back(), [router]);

  const [brand, setBrand] = useState<{
    context: MbcBrandContext | null;
    resolved: boolean;
  }>({ context: null, resolved: false });

  // Captured on mount: the chat writes its own threadId into the URL (so a
  // refresh can recover the session), and that must not flip a live fresh
  // conversation into resume mode mid-flight.
  const [isResume] = useState(() =>
    Boolean(resumeThreadId && resumeResourceId),
  );
  const { data: resumeBrand, isFetching: isFetchingResumeBrand } =
    useGetBrandById(resumeBrandId ?? 0);

  const handleCreateNewBrand = useCallback(() => {
    void navigate({ to: "/brands/create" });
  }, [navigate]);

  const shell = (children: React.ReactNode) => (
    <div className="mx-auto flex h-full min-h-0 max-w-[1600px] flex-col overflow-hidden bg-bg-tertiary">
      {children}
    </div>
  );

  if (isResume) {
    if (resumeBrandId && isFetchingResumeBrand) {
      return shell(
        <>
          <MBCChatHeader onBack={handleBack} />
          <p className="p-3xl text-sm text-text-tertiary">Loading draft…</p>
        </>,
      );
    }

    return shell(
      <MBCConversation
        brand={resumeBrand ? mapBrandContext(resumeBrand) : null}
        designType={designType}
        projectId={projectId}
        resume={{ resourceId: resumeResourceId!, threadId: resumeThreadId! }}
      />,
    );
  }

  if (brand.resolved) {
    return shell(
      <MBCConversation
        brand={brand.context}
        designType={designType}
        projectId={projectId}
      />,
    );
  }

  return shell(
    <>
      <MBCChatHeader onBack={handleBack} />

      <div className="flex min-h-0 flex-1 gap-3xl p-3xl">
        <BrandGate
          onCreateNewBrand={handleCreateNewBrand}
          onResolved={(context) => setBrand({ context, resolved: true })}
          projectId={projectId}
        />
      </div>
    </>,
  );
}
